/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { forwardRef, memo, useMemo } from 'react'
import { CustomContentProps, SnackbarContent, useSnackbar } from 'notistack'
import { Alert } from '@mui/lab'
import { Button } from '@vendorflow/components'
import { ButtonGroup } from '@mui/material'

export type CustomNotificationOptions = {
  dismissAction?: () => void
  primary?: {
    action?: () => void
    label?: string
  }
  secondary?: {
    action: () => void
    label: string
  }
}
export type BaseSnackbarProps = CustomContentProps & CustomNotificationOptions

const BaseSnackbar = forwardRef<HTMLDivElement, BaseSnackbarProps>(function BaseSnackbar(props, ref) {
  const { id, message, variant, primary, secondary, className, style } = props
  const { closeSnackbar } = useSnackbar()
  const severity = variant === 'default' ? 'info' : variant

  function onDismiss() {
    closeSnackbar(id)
  }

  const action = useMemo(() => {
    return (
      <ButtonGroup size="small">
        {secondary && (
          <Button
            css={buttonStyle}
            variant="text"
            color={severity}
            onClick={() => {
              onDismiss()
              secondary.action()
            }}
          >
            {secondary.label}
          </Button>
        )}
        <Button
          css={buttonStyle}
          variant="text"
          color={severity}
          onClick={() => {
            onDismiss()
            primary?.action?.()
          }}
        >
          {primary?.label || 'Dismiss'}
        </Button>
      </ButtonGroup>
    )
  }, [])

  return (
    <SnackbarContent css={contentStyle} className={className} style={style} ref={ref}>
      <Alert
        className={`snackbar-alert snackbar-alert--${severity}`}
        sx={{ width: '100%' }}
        severity={severity}
        elevation={2}
        action={action}
        data-testid="snackbar-alert"
      >
        {message}
      </Alert>
    </SnackbarContent>
  )
})

export default memo(BaseSnackbar)

const buttonStyle = css`
  font-weight: bolder;
`
const contentStyle = css`
  &.message-text--large .MuiAlert-message {
    font-size: 1.25rem;
  }
`
